import React from 'react';
import clsx from 'clsx';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Button } from './Button';

/**
 * ErrorState — canonical failed-load surface, sibling of EmptyState (PR-42).
 *
 * REQ-SYS-EMPTY-01 restated: `EMPTY` and `ERROR` are distinct states. A
 * request that returned zero rows and a request that FAILED must be visually
 * distinguishable to the user. Twelve times this iteration a substituted
 * empty array or default vocabulary rendered the two as identical — the
 * customer sees "no contractors configured" and there are 618 in the table.
 * This primitive is the load-bearing side of that split.
 *
 * When to use:
 *   - A list fetch REJECTED (network 5xx, auth 4xx, parse error). Use this.
 *   - A list fetch returned []. Use EmptyState.
 *   - Loading. Use Spinner.
 *   - Not permitted (401/403). Use NotPermittedState (or this with a specific
 *     title if that primitive doesn't exist yet — PR-42b).
 *
 * The `onRetry` prop enables the retry button; omit for a static error.
 *
 * Do not use this for fatal application errors (unhandled exceptions,
 * component crashes) — those go to `common/ErrorPage` at the route level.
 * This is a scoped, in-context failure state.
 */
export function ErrorState({
  title = 'Something went wrong',
  message,
  size = 'md',           // 'sm' (inline) | 'md' (card) | 'lg' (page)
  onRetry,               // omit to hide the retry button
  className,
  children,              // slot for a secondary action (e.g. contact support)
}) {
  const isSmall = size === 'sm';
  const isLarge = size === 'lg';

  return (
    <div
      role="alert"
      aria-live="polite"
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        isSmall ? 'py-4 px-3 gap-2' : isLarge ? 'py-12 px-6 gap-4' : 'py-8 px-4 gap-3',
        className,
      )}
      style={{
        backgroundColor: 'var(--color-error-bg)',
        border: '1px solid var(--color-error)',
        borderRadius: 'var(--r-base)',
      }}
    >
      <ExclamationTriangleIcon
        className={clsx(isSmall ? 'h-6 w-6' : isLarge ? 'h-12 w-12' : 'h-8 w-8')}
        style={{ color: 'var(--color-error)' }}
        aria-hidden="true"
      />
      <div
        style={{
          font: isLarge ? 'var(--text-h1)' : isSmall ? 'var(--text-body)' : 'var(--text-h2)',
          color: 'var(--color-text-primary)',
          fontWeight: isSmall ? 500 : 600,
        }}
      >
        {title}
      </div>
      {message && (
        <p
          className="text-sm max-w-md"
          style={{ color: 'var(--color-text-muted)' }}
        >
          {message}
        </p>
      )}
      {onRetry && (
        <Button
          onClick={onRetry}
          variant="secondary"
          size={isSmall ? 'sm' : 'md'}
          className="mt-1"
        >
          <ArrowPathIcon className="h-4 w-4 mr-2" aria-hidden="true" />
          Try again
        </Button>
      )}
      {children}
    </div>
  );
}

export default ErrorState;
